// @ts-nocheck
/**
 * connectAnalogInputsToMcu — bridge SPICE node voltages to MCU ADCs.
 *
 * Subscribes to the electrical store and injects voltages into the
 * running MCU sims (AVR / RP2040 / ESP32 QEMU) so that `analogRead`
 * in user sketches returns physically-correct values.
 *
 * Solver-agnostic by design: this module knows ONLY the electrical
 * store shape (`nodeVoltages`, `pinNetMap`, `timeWaveforms`) — it
 * doesn't care which solver produced them.  Replacing
 * CircuitScheduler with the CircuitSimulationService doesn't touch
 * this file.
 *
 * Three paths:
 *   • DC (.op): scalar `setAdcVoltage(sim, gpioPin, v)` per channel.
 *   • AC (.tran), AVR/RP2040: patch `onADCRead` so every analogRead
 *     samples the per-net waveform at the *exact wall-clock time*
 *     of the read.  Eliminates aliasing.
 *   • AC (.tran), ESP32: push the entire waveform to the QEMU MMIO
 *     via `setAdcWaveform` — QEMU does its own interpolation.
 *
 * Extracted from the legacy `subscribeToStore.ts::wireElectricalSolver`
 * during Phase 1c step C of the mixed-mode migration.
 */
import {
  useSimulatorStore,
  getBoardSimulator,
} from "@/services/velxio/store/useSimulatorStore";
import { useElectricalStore } from "@/services/velxio/store/useElectricalStore";
import { setAdcVoltage } from "@/lib/velxio/simulation/parts/partUtils";
import type { BoardKind } from "@/lib/velxio/types/board";
import { interpolateAt } from "@/lib/velxio/simulation/spice/waveformStats";

// Which Arduino-style pin name maps to which ADC channel, per board.
function adcRange(prefix: string, start: number, count: number) {
  return Array.from({ length: count }, (_, i) => ({
    pinName: `${prefix}${start + i}`,
    channel: i,
  }));
}

const ADC_6CH = adcRange("A", 0, 6);
const ADC_8CH = adcRange("A", 0, 8);
const ADC_16CH = adcRange("A", 0, 16);

const ADC_PIN_MAP: Partial<
  Record<BoardKind, Array<{ pinName: string; channel: number }>>
> = {
  // AVR
  "arduino-uno": ADC_6CH,
  "arduino-nano": ADC_8CH,
  "arduino-mega": ADC_16CH,
  // ATtiny85: ADC channels live on PORTB pins, not on A0..An.
  // Wires reference the chip's own pin names (PB5/PB2/PB4/PB3).
  //   PB5 -> ADC0  PB2 -> ADC1  PB4 -> ADC2  PB3 -> ADC3
  attiny85: [
    { pinName: "PB5", channel: 0 },
    { pinName: "PB2", channel: 1 },
    { pinName: "PB4", channel: 2 },
    { pinName: "PB3", channel: 3 },
  ],

  // RP2040
  "raspberry-pi-pico": [
    { pinName: "GP26", channel: 0 },
    { pinName: "GP27", channel: 1 },
    { pinName: "GP28", channel: 2 },
    { pinName: "GP29", channel: 3 },
  ],
  "pi-pico-w": [
    { pinName: "GP26", channel: 0 },
    { pinName: "GP27", channel: 1 },
    { pinName: "GP28", channel: 2 },
    { pinName: "GP29", channel: 3 },
  ],

  // ESP32
  esp32: adcRange("GPIO", 32, 8),
  "esp32-devkit-c-v4": adcRange("GPIO", 32, 8),
  "esp32-cam": adcRange("GPIO", 32, 8),
  "wemos-lolin32-lite": adcRange("GPIO", 32, 8),
  "esp32-s3": adcRange("GPIO", 1, 10),
  "xiao-esp32-s3": adcRange("GPIO", 1, 10),
  "arduino-nano-esp32": adcRange("A", 0, 8),
  "esp32-c3": adcRange("GPIO", 0, 6),
  "xiao-esp32-c3": adcRange("GPIO", 0, 6),
  "aitewinrobot-esp32c3-supermini": adcRange("GPIO", 0, 6),
};

function avrPinFromName(_name: string, channel: number): number {
  return 14 + channel;
}
function gpioPinFromName(name: string, _channel: number): number {
  const m = name.match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : -1;
}

const ADC_PIN_TO_GPIO: Partial<
  Record<BoardKind, (pinName: string, channel: number) => number>
> = {
  "arduino-uno": avrPinFromName,
  "arduino-nano": avrPinFromName,
  "arduino-mega": avrPinFromName,
  attiny85: avrPinFromName,

  "raspberry-pi-pico": gpioPinFromName,
  "pi-pico-w": gpioPinFromName,

  esp32: gpioPinFromName,
  "esp32-devkit-c-v4": gpioPinFromName,
  "esp32-cam": gpioPinFromName,
  "wemos-lolin32-lite": gpioPinFromName,
  "esp32-s3": gpioPinFromName,
  "xiao-esp32-s3": gpioPinFromName,
  "arduino-nano-esp32": avrPinFromName,
  "esp32-c3": gpioPinFromName,
  "xiao-esp32-c3": gpioPinFromName,
  "aitewinrobot-esp32c3-supermini": gpioPinFromName,
};

export function connectAnalogInputsToMcu(): () => void {
  const patchedAdcs = new WeakSet<object>();
  const qemuWaveformChannels = new Set<string>();
  // Phase 1d #10 — warn-once set so ADC-clip messages don't spam the
  // console.  Key is `${boardId}:${pinName}`.
  const clipWarned = new Set<string>();
  let replayStartMs = 0;
  let replayEpochLatched = false;

  function injectVoltagesIntoADC() {
    const { nodeVoltages, pinNetMap } = useElectricalStore.getState();
    const { boards } = useSimulatorStore.getState();
    for (const board of boards) {
      const adcPins = ADC_PIN_MAP[board.boardKind];
      if (!adcPins) continue;
      const sim = getBoardSimulator(board.id);
      if (!sim) continue;
      const vMax = board.boardKind.startsWith("esp32") ? 3.3 : 5.0;
      for (const { pinName, channel } of adcPins) {
        const netName = pinNetMap.get(`${board.id}:${pinName}`);
        if (!netName) continue;
        const v = nodeVoltages[netName];
        if (v == null) continue;
        const clamped = Math.max(0, Math.min(vMax, v));
        const gpioPin = ADC_PIN_TO_GPIO[board.boardKind]?.(pinName, channel);
        if (gpioPin != null) setAdcVoltage(sim, gpioPin, clamped);
      }
    }
  }

  function sampleWaveformAtNow(net: string): number | undefined {
    const { timeWaveforms } = useElectricalStore.getState();
    if (!timeWaveforms) return undefined;
    const samples = timeWaveforms.nodes.get(net);
    if (!samples) return undefined;
    const times = timeWaveforms.time;
    const periodS = times[times.length - 1];
    if (!(periodS > 0)) return undefined;
    const t = ((performance.now() - replayStartMs) / 1000) % periodS;
    return interpolateAt(times, samples, t);
  }

  function pushEsp32Waveforms() {
    const { boards } = useSimulatorStore.getState();
    const { pinNetMap, timeWaveforms } = useElectricalStore.getState();
    for (const board of boards) {
      const adcPins = ADC_PIN_MAP[board.boardKind];
      if (!adcPins) continue;
      const sim = getBoardSimulator(board.id);
      if (!sim) continue;
      const shim = sim as unknown as {
        setAdcWaveform?: (
          pin: number,
          samples: Uint16Array,
          periodNs: number,
        ) => boolean;
      };
      if (typeof shim.setAdcWaveform !== "function") continue;
      const gpioFn = ADC_PIN_TO_GPIO[board.boardKind];
      if (!gpioFn) continue;
      const boardId = board.id;
      const seen = new Set<number>();

      if (timeWaveforms && timeWaveforms.time.length > 1) {
        const period = timeWaveforms.time[timeWaveforms.time.length - 1];
        if (period > 0) {
          const periodNs = Math.round(period * 1e9);
          for (const { pinName, channel } of adcPins) {
            const net = pinNetMap.get(`${boardId}:${pinName}`);
            const samples = net ? timeWaveforms.nodes.get(net) : undefined;
            if (!samples || samples.length === 0) continue;
            const u12 = new Uint16Array(samples.length);
            // Phase 1d #10 — count clip events to surface ESP32 ADC
            // range violations once per pin.  If more than 10% of
            // samples land outside [0, 3.3] V, warn the user; a
            // rectifier without a clamp is the canonical case.
            let clipped = 0;
            let observedMin = Infinity;
            let observedMax = -Infinity;
            for (let i = 0; i < samples.length; i++) {
              const s = samples[i];
              if (s < observedMin) observedMin = s;
              if (s > observedMax) observedMax = s;
              if (s < 0 || s > 3.3) clipped++;
              const v = Math.max(0, Math.min(3.3, s));
              u12[i] = Math.round((v / 3.3) * 4095);
            }
            const clipKey = `${boardId}:${pinName}`;
            if (clipped > samples.length / 10 && !clipWarned.has(clipKey)) {
              clipWarned.add(clipKey);

              console.warn(
                `[adc-clip] ${clipKey}: ${clipped}/${samples.length} samples outside [0, 3.3] V (range ${observedMin.toFixed(2)}…${observedMax.toFixed(2)} V). ESP32 ADC reads will saturate at the rails. Add a divider or clamp if you need the full swing.`,
              );
            }
            const gpioPin = gpioFn(pinName, channel);
            if (gpioPin < 0) continue;
            shim.setAdcWaveform(gpioPin, u12, periodNs);
            qemuWaveformChannels.add(`${boardId}:${channel}`);
            seen.add(channel);
          }
        }
      }

      // Clear channels that previously had a waveform but don't now.
      for (const { pinName, channel } of adcPins) {
        const key = `${boardId}:${channel}`;
        if (qemuWaveformChannels.has(key) && !seen.has(channel)) {
          const gpioPin = gpioFn(pinName, channel);
          if (gpioPin < 0) continue;
          shim.setAdcWaveform(gpioPin, new Uint16Array(0), 0);
          qemuWaveformChannels.delete(key);
        }
      }
    }
  }

  function installAdcReadHooks() {
    pushEsp32Waveforms();
    const { boards } = useSimulatorStore.getState();
    for (const board of boards) {
      const adcPins = ADC_PIN_MAP[board.boardKind];
      if (!adcPins) continue;
      const sim = getBoardSimulator(board.id);
      if (!sim) continue;
      const adc = (
        sim as unknown as { getADC?: () => object | null }
      ).getADC?.();
      if (!adc || patchedAdcs.has(adc)) continue;
      const boardId = board.id;

      const channelToNet = new Map<number, string>();
      const refreshChannelMap = () => {
        channelToNet.clear();
        const { pinNetMap } = useElectricalStore.getState();
        for (const { pinName, channel } of adcPins) {
          const net = pinNetMap.get(`${boardId}:${pinName}`);
          if (net) channelToNet.set(channel, net);
        }
      };

      const isRp2040 = "resolution" in (adc as object);

      if (isRp2040) {
        const self = adc as unknown as {
          channelValues: number[];
          onADCRead: (channel: number) => void;
        };
        const originalOnADCRead = self.onADCRead.bind(self);
        self.onADCRead = function (channel: number) {
          if (channelToNet.size === 0) refreshChannelMap();
          const net = channelToNet.get(channel);
          const v = net ? sampleWaveformAtNow(net) : undefined;
          if (v != null) {
            const clamped = Math.max(0, Math.min(3.3, v));
            self.channelValues[channel] = Math.round((clamped / 3.3) * 4095);
          }
          originalOnADCRead(channel);
        };
        patchedAdcs.add(adc);
        continue;
      }

      const self = adc as unknown as {
        channelValues: Array<number | undefined>;
        referenceVoltage: number;
        sampleCycles: number;
        cpu: { addClockEvent: (fn: () => void, cycles: number) => void };
        completeADCRead: (value: number) => void;
        onADCRead: (input: {
          type: number;
          channel?: number;
          voltage?: number;
          positiveChannel?: number;
          negativeChannel?: number;
          gain?: number;
        }) => void;
      };
      self.onADCRead = function (input) {
        if (channelToNet.size === 0) refreshChannelMap();
        let voltage = 0;
        switch (input.type) {
          case 2: // Constant
            voltage = input.voltage ?? 0;
            break;
          case 0: {
            // SingleEnded
            const ch = input.channel ?? 0;
            const net = channelToNet.get(ch);
            const waveV = net ? sampleWaveformAtNow(net) : undefined;
            voltage = waveV ?? self.channelValues[ch] ?? 0;
            break;
          }
          case 1: {
            // Differential
            const pos = input.positiveChannel ?? 0;
            const neg = input.negativeChannel ?? 0;
            const gain = input.gain ?? 1;
            const vPos =
              sampleWaveformAtNow(channelToNet.get(pos) ?? "") ??
              self.channelValues[pos] ??
              0;
            const vNeg =
              sampleWaveformAtNow(channelToNet.get(neg) ?? "") ??
              self.channelValues[neg] ??
              0;
            voltage = gain * (vPos - vNeg);
            break;
          }
          case 3: // Temperature
            voltage = 0.378125;
            break;
        }
        const rawValue = (voltage / self.referenceVoltage) * 1024;
        const result = Math.min(Math.max(Math.floor(rawValue), 0), 1023);
        self.cpu.addClockEvent(
          () => self.completeADCRead(result),
          self.sampleCycles,
        );
      };
      patchedAdcs.add(adc);
    }
  }

  // Re-inject + re-hook on every solve result.
  const unsubResult = useElectricalStore.subscribe((state, prev) => {
    if (
      state.nodeVoltages !== prev.nodeVoltages ||
      state.timeWaveforms !== prev.timeWaveforms
    ) {
      injectVoltagesIntoADC();
      if (state.timeWaveforms && !replayEpochLatched) {
        replayStartMs = performance.now();
        replayEpochLatched = true;
      }
      installAdcReadHooks();
    }
  });

  // Re-hook when boards change (loadHex creates fresh ADC instances).
  const unsubBoards = useSimulatorStore.subscribe((state, prev) => {
    if (state.boards !== prev.boards) {
      const { nodeVoltages } = useElectricalStore.getState();
      if (Object.keys(nodeVoltages).length > 0) injectVoltagesIntoADC();
      installAdcReadHooks();
    }
  });

  // Initial pass — examples that pre-populate the store before mount.
  injectVoltagesIntoADC();
  installAdcReadHooks();

  return () => {
    unsubResult();
    unsubBoards();
  };
}
